import { Box, Button, Text, VisibleIcon } from "@minervaproject/fds";
import { useContext, useEffect, useRef, useState } from "react";

import { FeaturedPollResponseModal } from "./featured_poll_response_modal";
import { PollResponseForm } from "./poll_response_form";
import { PollResponses } from "./poll_responses";
import { PollVisibility } from "./poll_visibility";

import { endPollSessionHelper } from "api/api_helpers";
import { MobileCard, MobileCardHorizontalLine } from "components/shared/mobile_components";
import { PollChoices } from "components/stage/poll_session_pane/poll_choices";
import { FlightAPIContext } from "contexts/flight_api_context";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapabilities } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { makePollResponsesVisibleToStudents } from "shared/reducers/classroom";
import { FeaturedPollResponseType, PollSessionStatusType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";
import { safeMarkdownToHtml } from "utils/markdown";

function PollSessionPane({
  pollSessionId,
  featuredPollResponse,
}: {
  pollSessionId: string;
  featuredPollResponse?: FeaturedPollResponseType;
}) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const isMobile = useIsMobile();
  const flightAPI = useContext(FlightAPIContext);
  const pollSession = useAppSelector(classroomSelectors.selectPollSessionById(pollSessionId));
  const pollResponses = useAppSelector(
    classroomSelectors.selectPollResponsesByPollSessionId(pollSessionId),
  );
  const poll = useAppSelector(classroomSelectors.selectPollById(pollSession?.poll_id));
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const { canControlClassroom, canParticipateInClass, canSeePollResponsesAsTeacher } =
    useCapabilities();

  const pollCompleted = pollSession?.status === PollSessionStatusType.Completed;
  const canSeePollResponses =
    (canSeePollResponsesAsTeacher && !isPresentationView) ||
    (pollCompleted && poll?.student_visible);
  const canSeeEndPollButton = canControlClassroom && !pollCompleted && !isPresentationView;

  const handleEndPoll = async () => {
    if (!poll || !pollSession) return;
    endPollSessionHelper(
      pollSession,
      pollResponses,
      poll?.type,
      flightAPI.getServerMilliseconds(),
      dispatch,
      forumAPI,
    );
  };

  const handleMakePollResponsesVisibleToStudents = () => {
    if (poll) {
      dispatch(
        makePollResponsesVisibleToStudents({
          pollId: poll.id,
        }),
      );
    }
  };

  const containerId = `poll-session-pane-${pollSessionId}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      setContainerHeight(entries[0].contentRect.height);
      setContainerWidth(entries[0].contentRect.width);
    });
    const target = containerRef.current;
    if (target) observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, []);

  if (!pollSession || !poll) {
    return null;
  }

  const pollQuestionHtml = safeMarkdownToHtml(poll.question);

  if (isMobile) {
    return (
      <MobileCard>
        {!pollCompleted && (
          <PollVisibility textKind="body-s" isStudentVisible={poll.student_visible} />
        )}
        {/* eslint-disable-next-line react/no-danger */}
        <h4 className="h4 mt2" dangerouslySetInnerHTML={pollQuestionHtml} />
        <MobileCardHorizontalLine />
        {canParticipateInClass && (
          <PollResponseForm
            key={pollSessionId}
            poll={poll}
            pollSession={pollSession}
            userId={currentUserId}
          />
        )}
        {(canSeePollResponses || pollCompleted) && (
          <PollResponses
            poll={poll}
            pollSession={pollSession}
            canSeeAllPollResponses={canSeePollResponses || false}
            canControlClassroom={canControlClassroom}
            featuredPollResponse={featuredPollResponse}
            userId={currentUserId}
          />
        )}

        {canSeeEndPollButton && (
          <Box className="flex-none" mt={3}>
            <Button fullWidth kind="primary-mobile" onClick={handleEndPoll}>
              End Poll
            </Button>
          </Box>
        )}
      </MobileCard>
    );
  }

  return (
    <div
      className="w-full flex bg-white vertical-scroll p5"
      data-testid="poll-session-pane"
      id={containerId}
      ref={containerRef}
    >
      <div
        className="w-full flex flex-column"
        style={{
          maxWidth: isPresentationView ? "1600px" : "800px",
          marginLeft: "auto",
          marginRight: "auto",
          wordWrap: "break-word",
          flexGrow: 1,
        }}
      >
        {isPresentationView && (
          <div className="body-s">{pollCompleted ? "Poll Completed" : "Poll In Progress"}</div>
        )}
        <h3
          className="h3 text-black-tint-10 mb5 poll-question"
          style={{
            userSelect: "none",
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={pollQuestionHtml}
        />
        {canSeePollResponsesAsTeacher && !isPresentationView && !poll.student_visible && (
          <div className="flex items-center flex-wrap mb5">
            <VisibleIcon color="var(--black-tint-70)" width={12} height={12} mr={1} />
            <Text kind="body-xs" color="blackTint40">
              Responses viewable by instructor only.&nbsp;
            </Text>
            {canControlClassroom && (
              <button
                type="button"
                className="button-reset body-xs text-black-tint-40 cursor-pointer underline"
                onClick={handleMakePollResponsesVisibleToStudents}
              >
                Show responses to students{!pollCompleted && " when poll ends"}
              </button>
            )}
          </div>
        )}
        {(canSeePollResponses || pollCompleted) && (
          <PollResponses
            poll={poll}
            pollSession={pollSession}
            canSeeAllPollResponses={canSeePollResponses || false}
            canControlClassroom={canControlClassroom}
            featuredPollResponse={featuredPollResponse}
            userId={currentUserId}
          />
        )}
        {isPresentationView && !pollCompleted && <PollChoices poll={poll} />}
        {canParticipateInClass && (
          <PollResponseForm
            key={pollSessionId}
            poll={poll}
            pollSession={pollSession}
            userId={currentUserId}
          />
        )}
        {canSeeEndPollButton && (
          <Box className="flex-none" mt={3}>
            <Button className="mb4" onClick={handleEndPoll} data-testid="end-poll-button">
              End Poll
            </Button>
          </Box>
        )}
      </div>
      {featuredPollResponse && (
        <FeaturedPollResponseModal
          featuredPollResponse={featuredPollResponse}
          canControlClassroom={canControlClassroom}
          containerId={`#${containerId}`}
          containerHeight={containerHeight}
          containerWidth={containerWidth}
        />
      )}
    </div>
  );
}

export { PollSessionPane };
